import React, { useEffect, useRef, useState } from "react";
import { Palette, Sparkles, Upload, Trash2, Loader2, Wand2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useMode } from "@/context/ModeContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StylePicker } from "@/components/StylePicker";
import { DEFAULT_STYLE } from "@/lib/styles";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const KID_TYPES = [["character", "My Characters"], ["world", "My Worlds"], ["object", "My Objects"]];
const PRO_TYPES = [["character", "Sprites"], ["background", "Backgrounds"], ["object", "Objects"], ["icon", "Icons"], ["enemy", "Enemies"]];

export default function AssetStudio() {
  const { isKid } = useMode();
  const { user } = useAuth();
  const kidView = isKid || user.role === "kid";
  const TYPES = kidView ? KID_TYPES : PRO_TYPES;

  const [assets, setAssets] = useState([]);
  const [tab, setTab] = useState(TYPES[0][0]);
  const [prompt, setPrompt] = useState("");
  const [genType, setGenType] = useState(TYPES[0][0]);
  const [style, setStyle] = useState(DEFAULT_STYLE);
  const [generating, setGenerating] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/assets").then((r) => setAssets(r.data));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe what to create!"); return; }
    setGenerating(true);
    try {
      await api.post("/assets/generate", { prompt, type: genType, style });
      toast.success("Asset created! 🎨");
      setPrompt("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", file.name);
    fd.append("type", tab);
    try {
      await api.post("/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded!");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    e.target.value = "";
  };

  const del = async (id) => { await api.delete(`/assets/${id}`); load(); };

  const filtered = assets.filter((a) => a.type === tab);

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto">
      <PageHeader title="Asset Studio" icon={Palette} subtitle="Create and collect art for your games." />

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-6 brand-glow">
        <div className="flex items-center gap-2 font-display font-bold mb-3"><Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" /> Create with AI</div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && generate()}
            placeholder={kidView ? "a happy purple dragon" : "pixel-art space background, neon stars"} className="flex-1" data-testid="asset-prompt" />
          <Select value={genType} onValueChange={setGenType}>
            <SelectTrigger className="sm:w-44" data-testid="asset-gen-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={generate} disabled={generating} data-testid="asset-generate-btn" className="bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Generate
          </Button>
        </div>
        <div className="mt-4 pt-3 border-t border-[hsl(var(--border))]">
          <StylePicker value={style} onChange={setStyle} testid="asset-style-picker" />
        </div>
        <div className="mt-3">
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} data-testid="asset-file-input" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="rounded-full" data-testid="asset-upload-btn">
            <Upload className="w-4 h-4" /> Upload your own drawing
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setGenType(v); }} className="mb-5">
        <TabsList>{TYPES.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`asset-tab-${v}`}>{l}</TabsTrigger>)}</TabsList>
      </Tabs>

      {filtered.length === 0 ? (
        <EmptyState emoji="🎨" title="No assets yet" text="Generate one with AI or upload your own art." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {filtered.map((a) => (
            <div key={a.id} className="relative group rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden card-hover">
              <div className="aspect-square bg-[hsl(var(--secondary))] grid place-items-center">
                <img src={`${BACKEND}${a.url}`} alt={a.name} className="w-full h-full object-contain" loading="lazy" />
              </div>
              <div className="p-2 text-xs font-semibold truncate">{a.name}</div>
              <button onClick={() => del(a.id)} data-testid={`asset-del-${a.id}`}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white grid place-items-center opacity-0 group-hover:opacity-100 hover:bg-red-500 transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
              {a.source === "ai" && <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[#3b9eff] text-white">AI</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
